import { useForm } from "react-hook-form";
import { FormProvider, RHFDateRangePickerField } from "../../common/hook-form";
import {
  Box, Button, Card, CardContent, Chip, Grid, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from "@mui/material";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import useAllocationAnalytics from "./useAllocationAnalytics";
import React, { useMemo } from "react";
import { LoadingButton } from "@mui/lab";
import Loader from "../../common/Loader";
import { formatNumber, numberPrecision } from "../../common/util";
import PageTitle from "../../components/PageTitle";
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─── Colour palette ────────────────────────────────────────────────────────────
const PRIORITY_COLORS = ['#1565c0', '#2e7d32', '#6a1b9a', '#e65100', '#0277bd', '#c62828']

// ─── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd', info }) {
  return (
    <Box sx={{
      borderRadius: 2.5, border: '1px solid #e8e8e8', backgroundColor: '#fff',
      p: 2, height: '100%', display: 'flex', gap: 1.5, alignItems: 'flex-start', boxShadow: 'none',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, backgroundColor: bg, border: `1px solid ${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
            {label}
          </Typography>
          {info && (
            <Tooltip arrow placement='top' title={
              <Box sx={{ p: 0.5, maxWidth: 260 }}>
                <Typography sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>{info}</Typography>
              </Box>
            }>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: '#bdbdbd', cursor: 'pointer', '&:hover': { color: '#1565c0' } }} />
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#1a1a1a', lineHeight: 1.1, mt: 0.4 }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ color: '#9e9e9e', fontSize: '0.7rem', mt: 0.3 }}>{sub}</Typography>}
      </Box>
    </Box>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
function Analytics() {
  const [filters, setFilters] = React.useState(null)
  const { isFetching, data, dayIntervalArr } = useAllocationAnalytics(filters)

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        merchant: Yup.string().required('Please select a merchant'),
        fromDate: Yup.mixed().required('Please select a date range').nullable(),
        toDate: Yup.mixed().nullable(),
      })
    ),
    defaultValues: { merchant: '', fromDate: null, toDate: null }
  });

  const { handleSubmit, reset } = methods;
  const onSubmit = (filters) => setFilters(filters)

  // ── Derived KPIs ───────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!data || data.length === 0) return null
    const totalOrders = data.reduce((sum, r) => sum + r.count, 0)
    const p1Row = data.find((r) => r.name === 1) || {}
    const p1Count = p1Row.count || 0
    const p1Pct = totalOrders > 0 ? ((p1Count / totalOrders) * 100).toFixed(1) : '0.0'

    // Weighted avg priority
    let weightedSum = 0
    data.forEach((r) => {
      const pVal = r.name === '6+' ? 6 : Number(r.name)
      weightedSum += pVal * r.count
    })
    const avgPriority = totalOrders > 0 ? (weightedSum / totalOrders).toFixed(2) : '—'

    // Top 2 priority % (P1 + P2)
    const p2Row = data.find((r) => r.name === 2) || {}
    const topPct = totalOrders > 0 ? (((p1Count + (p2Row.count || 0)) / totalOrders) * 100).toFixed(1) : '0.0'

    return { totalOrders, p1Count, p1Pct, avgPriority, topPct }
  }, [data])

  const hasData = !isFetching && data.length > 0

  return (
    <>
      <PageTitle>Allocation Analytics</PageTitle>

      <Grid container spacing={2}>
        {/* ── Filter Card ── */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8e8e8', boxShadow: 'none' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterListIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
                  Filters
                </Typography>
              </Box>
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 250 }}>
                    <MerchantFilterCached />
                  </Box>
                  <Box sx={{ width: 300 }}>
                    <RHFDateRangePickerField name="created_date" label="Created Date" maxDate={new Date()} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <LoadingButton
                      loading={isFetching}
                      type="submit"
                      variant="contained"
                      size="small"
                      sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: 2, height: 40,
                        backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' },
                      }}
                    >
                      Apply Filters
                    </LoadingButton>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => { reset({ merchant: '', fromDate: null, toDate: null }); setFilters(null) }}
                      sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 2, height: 40, color: '#757575',
                        borderColor: '#e0e0e0', '&:hover': { borderColor: '#bdbdbd', backgroundColor: '#fafafa' },
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
              </FormProvider>
            </CardContent>
          </Card>
        </Grid>

        {isFetching && (
          <Grid item xs={12}><Loader /></Grid>
        )}

        {/* ── Empty State ── */}
        {!isFetching && data.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{
              backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5,
              p: 5, textAlign: 'center', boxShadow: 'none',
            }}>
              <BarChartIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#757575', mb: 0.5 }}>
                No analytics data to display
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e', maxWidth: 400, mx: 'auto' }}>
                Select a merchant and date range above to view priority allocation analytics.
              </Typography>
            </Box>
          </Grid>
        )}

        {/* ── KPI Cards ── */}
        {hasData && kpis && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<LocalShippingIcon sx={{ fontSize: 22, color: '#1565c0' }} />}
                label='Total Orders'
                value={formatNumber(kpis.totalOrders)}
                sub='In selected date range'
                color='#1565c0' bg='#e3f2fd'
                info='Total number of orders across all priority levels for the selected merchant and date range.'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<LooksOneIcon sx={{ fontSize: 22, color: '#2e7d32' }} />}
                label='Priority 1 Rate'
                value={`${kpis.p1Pct}%`}
                sub={`${formatNumber(kpis.p1Count)} orders at P1`}
                color='#2e7d32' bg='#e8f5e9'
                info='Percentage of orders where the courier booked was the top priority (P1) recommendation.'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<TrendingUpIcon sx={{ fontSize: 22, color: '#6a1b9a' }} />}
                label='P1 + P2 Rate'
                value={`${kpis.topPct}%`}
                sub='Top 2 priority hit rate'
                color='#6a1b9a' bg='#f3e5f5'
                info='Combined percentage of orders that were allocated to Priority 1 or Priority 2 couriers.'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<BarChartIcon sx={{ fontSize: 22, color: '#e65100' }} />}
                label='Avg Priority'
                value={kpis.avgPriority}
                sub='Weighted average'
                color='#e65100' bg='#fff3e0'
                info='Weighted average priority level. Lower is better (1 = always top priority). Values of 6+ are capped at 6.'
              />
            </Grid>
          </>
        )}

        {/* ── Data Table ── */}
        {hasData && (
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>
                    Priority Allocation Details
                  </Typography>
                  <Typography sx={{ color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2 }}>
                    Daily count and percentage breakdown by courier priority level
                  </Typography>
                </Box>
                <Chip
                  label={`${dayIntervalArr.length} day${dayIntervalArr.length !== 1 ? 's' : ''}`}
                  size='small'
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                />
              </Box>

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
                  <TableHead>
                    {/* Header row 1: grouped day columns */}
                    <TableRow>
                      <TableCell sx={{
                        fontWeight: 700, fontSize: '0.78rem', color: '#424242', backgroundColor: '#fafbfc',
                        borderBottom: 'none', position: 'sticky', left: 0, zIndex: 3, minWidth: 90,
                      }}>
                        Priority
                      </TableCell>
                      {dayIntervalArr.map((d) => (
                        <TableCell
                          key={d}
                          colSpan={2}
                          align="center"
                          sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#1565c0', backgroundColor: '#fafbfc', borderBottom: 'none', whiteSpace: 'nowrap' }}
                        >
                          {d}
                        </TableCell>
                      ))}
                      <TableCell
                        colSpan={2}
                        align="center"
                        sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#1a1a1a', backgroundColor: '#f0f4ff', borderBottom: 'none' }}
                      >
                        Total
                      </TableCell>
                    </TableRow>

                    {/* Header row 2: Count / % sub-headers */}
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#fafbfc', borderBottom: '2px solid #e0e0e0', position: 'sticky', left: 0, zIndex: 3 }} />
                      {dayIntervalArr.map((d) => (
                        <React.Fragment key={`sub-${d}`}>
                          <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', backgroundColor: '#fafbfc', borderBottom: '2px solid #e0e0e0', px: 1 }}>
                            Count
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', backgroundColor: '#fafbfc', borderBottom: '2px solid #e0e0e0', px: 1 }}>
                            %
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#424242', backgroundColor: '#f0f4ff', borderBottom: '2px solid #e0e0e0', px: 1 }}>
                        Count
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#424242', backgroundColor: '#f0f4ff', borderBottom: '2px solid #e0e0e0', px: 1 }}>
                        %
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.map((row, idx) => {
                      const maxCount = Math.max(...data.map((r) => r.count)) || 1
                      const barPct = row.count > 0 ? Math.min((row.count / maxCount) * 100, 100) : 0

                      return (
                        <TableRow
                          key={row.name}
                          sx={{ '&:hover': { backgroundColor: '#f8f9ff' }, '&:last-child td': { borderBottom: 0 } }}
                        >
                          {/* Priority label */}
                          <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, borderRight: '1px solid #f0f0f0' }}>
                            <Chip
                              label={`P${row.name}`}
                              size='small'
                              sx={{
                                fontWeight: 700, fontSize: '0.75rem', height: 24, minWidth: 40,
                                backgroundColor: PRIORITY_COLORS[idx % PRIORITY_COLORS.length] + '18',
                                color: PRIORITY_COLORS[idx % PRIORITY_COLORS.length],
                              }}
                            />
                          </TableCell>

                          {/* Day columns */}
                          {dayIntervalArr.map((d) => {
                            const count = row[`${d}_Count`] || 0
                            const pct = row[`${d}_Percentage`] || 0
                            return (
                              <React.Fragment key={`${row.name}-${d}`}>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: count > 0 ? 600 : 400, color: count > 0 ? '#1a1a1a' : '#d0d0d0', px: 1 }}>
                                  {count > 0 ? formatNumber(count) : '—'}
                                </TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.75rem', color: pct > 0 ? '#757575' : '#d0d0d0', px: 1 }}>
                                  {pct > 0 ? `${numberPrecision(pct)}%` : '—'}
                                </TableCell>
                              </React.Fragment>
                            )
                          })}

                          {/* Total columns */}
                          <TableCell align="center" sx={{ backgroundColor: '#f8faff', borderLeft: '1px solid #e8e8e8' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a1a' }}>
                                {formatNumber(row.count)}
                              </Typography>
                              <LinearProgress
                                variant='determinate'
                                value={barPct}
                                sx={{
                                  width: '80%', height: 3, borderRadius: 2, backgroundColor: '#f0f0f0',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 2,
                                    backgroundColor: PRIORITY_COLORS[idx % PRIORITY_COLORS.length],
                                  },
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ backgroundColor: '#f8faff' }}>
                            <Chip
                              label={`${numberPrecision(row.percentage)}%`}
                              size='small'
                              sx={{
                                fontWeight: 700, fontSize: '0.72rem', height: 20,
                                backgroundColor: row.percentage > 30 ? '#e8f5e9' : row.percentage > 10 ? '#e3f2fd' : '#fafafa',
                                color: row.percentage > 30 ? '#2e7d32' : row.percentage > 10 ? '#1565c0' : '#9e9e9e',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}

                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                      <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: '#f5f7fa', zIndex: 1, borderRight: '1px solid #f0f0f0' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#1565c0' }}>Total</Typography>
                      </TableCell>
                      {dayIntervalArr.map((d) => {
                        const dayTotal = data.reduce((sum, r) => sum + (r[`${d}_Count`] || 0), 0)
                        return (
                          <React.Fragment key={`total-${d}`}>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1a1a1a', px: 1 }}>
                              {dayTotal > 0 ? formatNumber(dayTotal) : '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem', color: '#757575', px: 1 }}>
                              {dayTotal > 0 ? '100%' : '—'}
                            </TableCell>
                          </React.Fragment>
                        )
                      })}
                      <TableCell align="center" sx={{ backgroundColor: '#e8eeff', fontWeight: 800, fontSize: '0.85rem', color: '#1565c0', borderLeft: '1px solid #e8e8e8' }}>
                        {formatNumber(data.reduce((sum, r) => sum + r.count, 0))}
                      </TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#e8eeff', fontWeight: 800, fontSize: '0.78rem', color: '#1565c0' }}>
                        100%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default Analytics;
